import React, { useEffect, useState } from 'react';
import { MobileAppStoreKind } from '../../../../../../networked-shared-interfaces/community-admin/community-mobile-app';
import { ModelStatus } from '../../../../../../networked-shared-interfaces/constants/model-status';
import { INetworkedPlusPlan } from '../../../../../../networked-shared-interfaces/stripe-routes/networked-plus-plans';
import { useMobileAppStatus } from '../../hooks/useMobileAppStatus';

interface IMobileAppStatusPanelProps {
	communityId: string;
}

/** The overall record's transitions, in the order staff move through them. */
const OVERALL_STATUS_OPTIONS: { value: number; label: string }[] = [
	{ value: ModelStatus.MobileAppContractPending, label: 'Contract Pending' },
	{ value: ModelStatus.MobileAppPaid, label: 'Paid' },
	{ value: ModelStatus.MobileAppInDevelopment, label: 'In Development' },
	{ value: ModelStatus.MobileAppSubmittedToStores, label: 'Submitted to Stores' },
	{ value: ModelStatus.MobileAppLive, label: 'Live' },
	{ value: ModelStatus.MobileAppCancelled, label: 'Cancelled' },
];

const STORE_STATUS_OPTIONS: { value: number; label: string }[] = [
	{ value: ModelStatus.MobileAppStoreSubmitted, label: 'Submitted' },
	{ value: ModelStatus.MobileAppStoreInReview, label: 'In Review' },
	{ value: ModelStatus.MobileAppStorePendingRelease, label: 'Pending Release' },
	{ value: ModelStatus.MobileAppStoreAccepted, label: 'Accepted' },
	{ value: ModelStatus.MobileAppStoreRejected, label: 'Rejected' },
];

const STORES: { value: MobileAppStoreKind; label: string }[] = [
	{ value: MobileAppStoreKind.Apple, label: 'Apple App Store' },
	{ value: MobileAppStoreKind.Google, label: 'Google Play' },
];

const labelFor = (options: { value: number; label: string }[], value: number | null): string =>
	options.find(o => o.value === value)?.label ?? (value === null ? '—' : String(value));

const formatDate = (epochMillis: number | null): string =>
	epochMillis ? new Date(epochMillis).toLocaleString() : '—';

/**
 * Staff control for one community's branded mobile app.
 *
 * This is the only place a record can be moved forward. Fulfilment is entirely manual — the
 * Flutter app's white-label flavors are hardcoded across six build files plus a CI allowlist —
 * so the customer's progress screen shows exactly what somebody clicks here.
 */
const MobileAppStatusPanel: React.FC<IMobileAppStatusPanelProps> = ({ communityId }) => {
	const { loading, error, record, fetchRecord, setStatus, setStoreStatus, markPaid, fetchPlusPlans } =
		useMobileAppStatus(communityId);

	const [nextStatus, setNextStatus] = useState<number>(ModelStatus.MobileAppInDevelopment);
	const [statusNote, setStatusNote] = useState('');
	const [paidNote, setPaidNote] = useState('');
	const [paidPlanId, setPaidPlanId] = useState('');
	const [plusPlans, setPlusPlans] = useState<INetworkedPlusPlan[] | null>(null);

	const [store, setStore] = useState<number>(MobileAppStoreKind.Apple);
	const [versionLabel, setVersionLabel] = useState('');
	const [storeStatus, setStoreStatusValue] = useState<number>(ModelStatus.MobileAppStoreSubmitted);
	const [storeNotes, setStoreNotes] = useState('');

	useEffect(() => {
		fetchRecord();
	}, [fetchRecord]);

	const isPaid = Boolean(record?.paidAt || record?.paidOffline);
	// Paid offline before a plan was required: the token sync cannot resolve its grant, so the
	// community sits on Free tokens. The same picker sets the plan (Spencer's review Q15).
	const needsOfflinePlan = Boolean(record?.paidOffline && !record?.planId);
	const showMarkPaid = Boolean(record) && (!isPaid || needsOfflinePlan);

	// The tiers come from Stripe, so the planId sent is always one the token sync resolves. Loaded
	// only while the picker is on screen; pre-selects the plan the customer chose at intake, if any.
	useEffect(() => {
		if (!showMarkPaid || plusPlans !== null) return;
		fetchPlusPlans().then(plans => {
			setPlusPlans(plans);
			const chosen = record?.planId && plans.find(p => p.planId === record.planId);
			if (chosen) setPaidPlanId(chosen.planId);
		});
	}, [showMarkPaid, plusPlans, fetchPlusPlans, record?.planId]);

	const handleSetStatus = async () => {
		const ok = await setStatus(nextStatus, statusNote.trim() || undefined);
		if (ok) setStatusNote('');
	};

	const handleMarkPaid = async () => {
		if (!paidPlanId) return;
		const ok = await markPaid(paidPlanId, paidNote.trim() || undefined);
		if (ok) setPaidNote('');
	};

	const handleStoreStatus = async () => {
		if (!versionLabel.trim()) return;
		const ok = await setStoreStatus(store, versionLabel.trim(), storeStatus, storeNotes.trim() || undefined);
		if (ok) setStoreNotes('');
	};

	if (!record && loading) {
		return (
			<div className="card border-0 shadow-sm mt-3">
				<div className="card-body p-4 text-center text-muted">
					<i className="fas fa-spinner fa-spin mr-2"></i>Loading mobile app record…
				</div>
			</div>
		);
	}

	if (record && !record.configured) {
		// Not an error: most communities have never asked for a branded app.
		return (
			<div className="card border-0 shadow-sm mt-3">
				<div className="card-body p-4">
					<h6 className="font-weight-bold mb-2">Branded Mobile App</h6>
					<div className="alert alert-light border mb-0 py-2">
						<i className="fas fa-info-circle mr-2"></i>
						This community has not requested a branded app. Nothing to manage until they submit the
						form on <code>/admin-pages/mobile-app</code>.
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="card border-0 shadow-sm mt-3">
			<div className="card-body p-4">
				<div className="d-flex align-items-center mb-3">
					<div className="mr-3">
						<i className="fas fa-mobile-alt fa-lg text-primary"></i>
					</div>
					<div>
						<h6 className="mb-0 font-weight-bold">Branded Mobile App</h6>
						<small className="text-muted">
							{record?.details?.appName || 'Unnamed app'}
							{record?.planId ? ` · ${record.planId}` : ''}
						</small>
					</div>
				</div>

				{error && (
					<div className="alert alert-danger py-2">
						<i className="fas fa-exclamation-triangle mr-2"></i>{error}
					</div>
				)}

				{/* ── Current state ───────────────────────────────────────────── */}
				<div className="row mb-3">
					<div className="col-md-4">
						<label className="small text-muted mb-1 d-block">Status</label>
						<strong>{labelFor(OVERALL_STATUS_OPTIONS, record?.status ?? null)}</strong>
					</div>
					<div className="col-md-4">
						<label className="small text-muted mb-1 d-block">Paid</label>
						<strong>
							{isPaid
								? `${formatDate(record?.paidAt ?? null)}${record?.paidOffline ? ' (offline)' : ''}`
								: 'Not paid'}
						</strong>
					</div>
					<div className="col-md-4">
						<label className="small text-muted mb-1 d-block">Terms accepted</label>
						<strong>{formatDate(record?.termsAcceptedAt ?? null)}</strong>
					</div>
				</div>

				{/* ── Mark paid ───────────────────────────────────────────────── */}
				{showMarkPaid && (
					<div className="alert alert-warning py-3">
						<div className="mb-2">
							<i className="fas fa-lock mr-2"></i>
							{needsOfflinePlan ? (
								<>
									<strong>No plan on this offline payment.</strong> The community's token allowance comes
									from its Plus plan, so until one is set it stays on Free tokens. Choose the plan that was
									paid for.
								</>
							) : (
								<>
									<strong>Payment gates development.</strong> This record cannot move past Paid until
									money is in. Use this only when the customer has been invoiced offline — a custom-priced
									or enterprise deal with no self-serve amount to charge. Choose the Plus plan that was
									paid for: it sets the community's token allowance.
								</>
							)}
						</div>
						<div className="input-group input-group-sm">
							<select
								className="form-control"
								style={{ maxWidth: 260 }}
								aria-label="Plus plan paid for"
								value={paidPlanId}
								disabled={plusPlans === null}
								onChange={e => setPaidPlanId(e.target.value)}
							>
								<option value="">
									{plusPlans === null
										? 'Loading plans…'
										: plusPlans.length === 0
											? 'No Plus plans found in Stripe'
											: 'Choose the Plus plan…'}
								</option>
								{(plusPlans ?? []).map(p => (
									<option key={p.planId} value={p.planId}>
										{p.name} ({p.planId})
									</option>
								))}
							</select>
							{!needsOfflinePlan && (
								<input
									type="text"
									className="form-control"
									placeholder="Invoice reference or PO number (optional)"
									value={paidNote}
									onChange={e => setPaidNote(e.target.value)}
								/>
							)}
							<div className="input-group-append">
								<button
									className="btn btn-warning"
									type="button"
									disabled={loading || !paidPlanId}
									onClick={handleMarkPaid}
								>
									{needsOfflinePlan ? 'Set plan' : 'Mark as paid'}
								</button>
							</div>
						</div>
					</div>
				)}

				{/* ── Advance overall status ──────────────────────────────────── */}
				<div className="border rounded p-3 mb-3">
					<label className="small text-muted mb-2 d-block font-weight-bold">Advance status</label>
					<div className="form-row">
						<div className="col-md-4 mb-2">
							<select
								className="form-control form-control-sm"
								value={nextStatus}
								onChange={e => setNextStatus(Number(e.target.value))}
							>
								{OVERALL_STATUS_OPTIONS.map(o => (
									<option key={o.value} value={o.value}>{o.label}</option>
								))}
							</select>
						</div>
						<div className="col-md-6 mb-2">
							<input
								type="text"
								className="form-control form-control-sm"
								placeholder="Why (optional) — shows in the customer's history"
								value={statusNote}
								onChange={e => setStatusNote(e.target.value)}
							/>
						</div>
						<div className="col-md-2 mb-2">
							<button
								className="btn btn-primary btn-sm btn-block"
								type="button"
								disabled={loading}
								onClick={handleSetStatus}
							>
								Update
							</button>
						</div>
					</div>
					{nextStatus === ModelStatus.MobileAppLive && (
						<small className="text-muted d-block">
							<i className="fas fa-info-circle mr-1"></i>
							Setting Live also marks this community as Networked Plus, which switches invite emails to
							the white-label wording.
						</small>
					)}
				</div>

				{/* ── Per-store review ────────────────────────────────────────── */}
				<div className="border rounded p-3 mb-3">
					<label className="small text-muted mb-2 d-block font-weight-bold">Store review</label>
					<p className="small text-muted">
						Apple and Google move independently — one can be accepted while the other is rejected.
						Updating a store never changes the overall status; set that separately once both are in.
					</p>
					<div className="form-row">
						<div className="col-md-3 mb-2">
							<select
								className="form-control form-control-sm"
								value={store}
								onChange={e => setStore(Number(e.target.value))}
							>
								{STORES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
							</select>
						</div>
						<div className="col-md-2 mb-2">
							<input
								type="text"
								className="form-control form-control-sm"
								placeholder="Version"
								value={versionLabel}
								onChange={e => setVersionLabel(e.target.value)}
							/>
						</div>
						<div className="col-md-3 mb-2">
							<select
								className="form-control form-control-sm"
								value={storeStatus}
								onChange={e => setStoreStatusValue(Number(e.target.value))}
							>
								{STORE_STATUS_OPTIONS.map(o => (
									<option key={o.value} value={o.value}>{o.label}</option>
								))}
							</select>
						</div>
						<div className="col-md-2 mb-2">
							<input
								type="text"
								className="form-control form-control-sm"
								placeholder="Notes"
								value={storeNotes}
								onChange={e => setStoreNotes(e.target.value)}
							/>
						</div>
						<div className="col-md-2 mb-2">
							<button
								className="btn btn-primary btn-sm btn-block"
								type="button"
								disabled={loading || !versionLabel.trim()}
								onClick={handleStoreStatus}
							>
								Save
							</button>
						</div>
					</div>

					{record?.stores?.length ? (
						<table className="table table-sm mb-0">
							<thead>
								<tr>
									<th>Store</th><th>Version</th><th>Status</th><th>Submitted</th><th>Decided</th><th>Notes</th>
								</tr>
							</thead>
							<tbody>
								{record.stores.map(s => (
									<tr key={s.id}>
										<td>{labelFor(STORES, s.store)}</td>
										<td>{s.versionLabel}</td>
										<td>{labelFor(STORE_STATUS_OPTIONS, s.status)}</td>
										<td>{formatDate(s.submittedAt)}</td>
										<td>{formatDate(s.decidedAt)}</td>
										<td>{s.notes || '—'}</td>
									</tr>
								))}
							</tbody>
						</table>
					) : (
						<small className="text-muted">No builds submitted yet.</small>
					)}
				</div>

				{/* ── History ─────────────────────────────────────────────────── */}
				<div className="border rounded p-3">
					<label className="small text-muted mb-2 d-block font-weight-bold">History</label>
					{record?.statusLog?.length ? (
						<table className="table table-sm mb-0">
							<thead>
								<tr><th>When</th><th>Change</th><th>Store</th><th>By</th><th>Note</th></tr>
							</thead>
							<tbody>
								{record.statusLog.map(l => (
									<tr key={l.id}>
										<td>{formatDate(l.timeStamp)}</td>
										<td>
											{l.fromStatus === l.toStatus
												? 'Details edited'
												: `${labelFor([...OVERALL_STATUS_OPTIONS, ...STORE_STATUS_OPTIONS], l.fromStatus)} → ${labelFor([...OVERALL_STATUS_OPTIONS, ...STORE_STATUS_OPTIONS], l.toStatus)}`}
										</td>
										<td>{l.store === null ? '—' : labelFor(STORES, l.store)}</td>
										{/* Null actor means the system moved it, e.g. a Stripe webhook. */}
										<td>{l.actorName || (l.actorUserId ? l.actorUserId : 'System')}</td>
										<td>{l.note || '—'}</td>
									</tr>
								))}
							</tbody>
						</table>
					) : (
						<small className="text-muted">Nothing recorded yet.</small>
					)}
				</div>
			</div>
		</div>
	);
};

export default MobileAppStatusPanel;
